import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAdminListKycQuery } from '../../features/kyc/kycApi';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { MobileCard, DataRow } from '../../components/ui/MobileCard';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { authErrorMessage } from '../../lib/authErrorMessage';

const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'danger' };

const DOCUMENT_TYPE_LABEL = {
  national_id: 'National ID',
  passport: 'Passport',
  drivers_license: "Driver's License",
  utility_bill: 'Utility Bill',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * GET /admin/kyc?status=...&page=N — a real Laravel paginator (confirmed
 * live), not a flat array. `isError` here means the HTTP request itself
 * failed (network/timeout/4xx/5xx) — a shape mismatch on a *successful*
 * response never reaches that path anymore (see kycApi's parseError
 * handling), so it gets its own distinct message instead of masquerading
 * as "couldn't reach the server."
 */
export default function KycReview() {
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError, error } = useAdminListKycQuery({ status, page });

  const changeTab = (value) => {
    setStatus(value);
    setPage(1);
  };

  const submissions = data?.submissions ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">KYC review queue</h1>
          <Badge tone="success">Live</Badge>
        </div>
        <p className="text-foreground-muted text-sm mt-1">Review and approve investor identity verification submissions.</p>
      </div>

      <Card>
        <CardHeader
          title="Submissions"
          subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined}
          action={
            <div className="flex gap-1 rounded-lg bg-background-elevated p-1">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => changeTab(t.value)}
                  className={clsx(
                    'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                    status === t.value ? 'bg-accent-soft text-accent' : 'text-foreground-muted hover:text-foreground'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          }
        />

        {isLoading && <SkeletonTable columns={['Investor', 'Document type', 'Submitted', 'Status']} />}

        {isError && (
          <p className="py-8 text-center text-danger">
            {authErrorMessage(error, { fallback: 'Could not load KYC submissions.' })}
          </p>
        )}

        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">
            Something went wrong loading this data. The response didn't match what this screen expected — check the console
            for details.
          </p>
        )}

        {!isLoading && !isError && !parseError && submissions.length === 0 && (
          <p className="py-8 text-center text-foreground-muted">
            No {status === 'all' ? '' : status} submissions{status === 'all' ? ' yet' : ''}.
          </p>
        )}

        {!isLoading && !isError && !parseError && submissions.length > 0 && (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-foreground-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">Investor</th>
                    <th className="py-2 pr-4 font-medium">Document type</th>
                    <th className="py-2 pr-4 font-medium">Submitted</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {submissions.map((s) => (
                    <tr
                      key={s.id}
                      className="cursor-pointer hover:bg-background-elevated transition-colors"
                      onClick={() => navigate(`/admin/kyc/${s.id}`)}
                    >
                      <td className="py-3 pr-4 text-foreground">{s.user?.full_name || '—'}</td>
                      <td className="py-3 pr-4 text-foreground-muted">{DOCUMENT_TYPE_LABEL[s.document_type] || s.document_type}</td>
                      <td className="py-3 pr-4 text-foreground-muted">{formatDate(s.created_at)}</td>
                      <td className="py-3">
                        <Badge tone={STATUS_TONE[s.status] || 'neutral'}>{s.status?.toUpperCase()}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {submissions.map((s) => (
                <MobileCard key={s.id} className="cursor-pointer" onClick={() => navigate(`/admin/kyc/${s.id}`)}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">{s.user?.full_name || '—'}</p>
                    <Badge tone={STATUS_TONE[s.status] || 'neutral'}>{s.status?.toUpperCase()}</Badge>
                  </div>
                  <DataRow label="Document type" value={DOCUMENT_TYPE_LABEL[s.document_type] || s.document_type} />
                  <DataRow label="Submitted" value={formatDate(s.created_at)} />
                </MobileCard>
              ))}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage <= 1}
                    onClick={() => setPage(Math.max(1, meta.currentPage - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
